import { useState, useEffect } from 'react';
import type { SyntheticEvent } from 'react';
import useQuestionBankContext from '../../../../api/QuestionBankContext';
import DescriptionTextArea from '../../../../components/forms/DescriptionTextAreaQuill';
import InputCheckbox from '../../../../components/forms/InputCheckbox';
import SelectElement from '../../../../components/forms/SelectElement';
import TextInputForm from '../../../../components/forms/TextInputForm';
import Headline from '../../../../components/ui/Headline';
import {
	prepPlusSistemas,
	prepPlusTemas,
	testLabelOptions
} from '../../../../data/option-objects/examOptions';
import UploadFile from '../../utils/UploadFile';
import SubmitChanges from './SubmitChanges';
import type {
	ExamLabel,
	SubCategory,
	CreateQuestionData
} from '../../../../data/interface-question';
import type { QuestionBankID } from '../../../../data/interface-question-bank';
import type { OptionObjMustHaves } from '../../../../data/interface-utils';

interface QuestionBuilderFormProps {
	questionBankID: QuestionBankID;
	onSubmit: (formData: CreateQuestionData) => void;
	onClear: () => void;
}

const QuestionBuilderForm = ({
	questionBankID,
	onSubmit,
	onClear
}: QuestionBuilderFormProps) => {
	const { categories } = useQuestionBankContext();

	// Internal form state
	const [isFormValid, setIsFormValid] = useState(false);
	const [isFormSubmitted, _setIsFormSubmitted] = useState(false);
	const [source, setSource] = useState<ExamLabel>();
	const [categoryID, setCategoryID] = useState<string>();
	const [showSubCategory, setShowSubCategory] = useState<boolean>(false);
	const [subCategory, setSubCategory] = useState<string>();
	const [question, setQuestion] = useState<string>('');
	const [answer, setAnswer] = useState<string>('');
	const [option1, setOption1] = useState<string>('');
	const [option2, setOption2] = useState<string>('');
	const [option3, setOption3] = useState<string>('');
	const [explanation, setExplanation] = useState<string>('');
	const [fuentes, setFuentes] = useState<string>('');
	const [showImageInQuestion, setShowImageInQuestion] =
		useState<boolean>(false);
	const [tags, setTags] = useState<string[]>([]);
	const [audioPath, setAudioPath] = useState<string>();
	const [imagePath, setImagePath] = useState<string>();
	const [prepPlusTema, setPrepPlusTema] = useState<string>();
	const [prepPlusSistema, setPrepPlusSistema] = useState<string>();

	// #region CATEGORIES
	const loadCategories = (): OptionObjMustHaves[] => {
		const cats: OptionObjMustHaves[] = [];
		categories
			?.filter((c) => c.questionBank === questionBankID)
			.map((cat) => {
				return cats.push({ id: cat.id, value: cat.name });
			});
		return cats;
	};

	const loadSubCategories = (): OptionObjMustHaves[] => {
		const sCO: OptionObjMustHaves[] = [];
		const subCategories = categories
			?.filter((c) => c.questionBank === questionBankID)
			.find((c) => c.id === categoryID)?.subCategoryList;
		if (subCategories !== undefined) {
			subCategories.map((sc: SubCategory) => {
				return sCO.push({
					id: sc.name.toLowerCase(),
					value: sc.name
				});
			});
		}
		return sCO;
	};
	// #endregion

	const checkChanges = () => {
		if (
			question !== '' &&
			source !== undefined &&
			categoryID !== undefined &&
			answer !== '' &&
			option1 !== '' &&
			option2 !== '' &&
			option3 !== '' &&
			explanation !== '' &&
			fuentes !== ''
		) {
			setIsFormValid(true);
		} else {
			setIsFormValid(false);
		}
	};

	const handleCategoryChange = (v: string) => {
		setCategoryID(v);
		const subCategories = categories
			?.filter((c) => c.questionBank === questionBankID)
			.find((c) => c.id === v)?.subCategoryList;
		if (subCategories !== undefined) {
			setShowSubCategory(true);
		}
	};

	const handleTags = (e: SyntheticEvent) => {
		const target = e.target as HTMLInputElement;
		const { id } = target;
		const tagsArray: string[] = tags.length > 0 ? [...tags] : [];

		if (tags.includes(id)) {
			const index = tagsArray.indexOf(id);
			tagsArray.splice(index, 1);
			setTags([...tagsArray]);
		} else {
			tagsArray.push(id);
			setTags([...tagsArray]);
		}
	};

	const handleSubmit = (e: SyntheticEvent) => {
		e.preventDefault();
		if (
			categoryID !== undefined &&
			source !== undefined &&
			question !== '' &&
			answer !== '' &&
			option1 !== '' &&
			option2 !== '' &&
			option3 !== '' &&
			explanation !== '' &&
			fuentes !== ''
		) {
			const formData: CreateQuestionData = {
				bank: questionBankID,
				categoryID,
				question,
				source,
				option1,
				option2,
				option3,
				tags: [],
				subCategoryList: '',
				isPruebaDiagnostica:
					source === 'evaluacion-1' || source === 'evaluacion-2',
				answer,
				explanation,
				fuente: fuentes
			};

			if (subCategory !== undefined && subCategory !== '') {
				formData.subCategoryList = subCategory;
			}

			if (tags.length > 0) {
				formData.tags = tags;
			}

			if (imagePath !== undefined && imagePath !== '') {
				formData.imageFileName = imagePath;
				formData.showImageInQuestion = showImageInQuestion;
			}

			if (audioPath !== undefined && audioPath !== '') {
				formData.audioFileName = audioPath;
			}

			if (prepPlusTema !== undefined && prepPlusTema !== '') {
				formData.tema = prepPlusTema;
			}

			if (prepPlusSistema !== undefined && prepPlusSistema !== '') {
				formData.sistema = prepPlusSistema;
			}

			onSubmit(formData);
		}
	};

	const clearForm = () => {
		onClear();
	};

	// Check form validity whenever form fields change
	useEffect(() => {
		checkChanges();
	}, [
		question,
		source,
		categoryID,
		answer,
		option1,
		option2,
		option3,
		explanation,
		fuentes
	]);

	return (
		<form onSubmit={handleSubmit}>
			<SelectElement
				defaultOption={`- Elige Uno: ${questionBankID} o prepmed -`}
				id='source'
				icon='ballot'
				iconFam='material-icons'
				label='Elige un tipo de examen'
				value={source ?? 'default'}
				handleChange={(v) => {
					setSource(v as ExamLabel);
				}}
				options={
					questionBankID === 'enurm'
						? testLabelOptions.enurmAdmin
						: questionBankID === 'prep+'
						? testLabelOptions['prep+']
						: questionBankID === 'maxilofacial'
						? testLabelOptions.maxilofacialAdmin
						: questionBankID === 'dermatología'
						? testLabelOptions.dermatología
						: []
				}
			/>
			<SelectElement
				defaultOption='Elige una categoría'
				id='categoryID'
				icon='ballot'
				iconFam='material-icons'
				label='Elige una categoría'
				options={loadCategories()}
				value={categoryID ?? 'default'}
				handleChange={(v) => {
					handleCategoryChange(v);
				}}
			/>
			{showSubCategory && (
				<SelectElement
					isDefaultOptionDisabled={false}
					defaultOption='Elige una subcategoría'
					id='subCategoryList'
					icon='ballot'
					iconFam='material-icons'
					label='Elige una subcategoría'
					options={loadSubCategories()}
					value=''
					handleChange={(v) => {
						setSubCategory(v);
					}}
				/>
			)}

			{/* // TODO: Add 'SELECT A SPECIALIZATION' */}

			{questionBankID === 'prep+' && (
				<>
					<SelectElement
						isDefaultOptionDisabled={false}
						defaultOption='Elige un tema'
						id='tema'
						icon='ballot'
						iconFam='material-icons'
						label='Elige una subcategoría'
						options={prepPlusTemas}
						value=''
						handleChange={(v) => {
							setPrepPlusTema(v);
						}}
					/>
					<SelectElement
						isDefaultOptionDisabled={false}
						defaultOption='Elige un sistema'
						id='sistema'
						icon='ballot'
						iconFam='material-icons'
						label='Elige una subcategoría'
						options={prepPlusSistemas}
						value=''
						handleChange={(v) => {
							setPrepPlusSistema(v);
						}}
					/>
				</>
			)}
			<DescriptionTextArea
				id='question'
				infoMessage='Escribe la pregunta que el usuario debe ver...'
				isFormSubmitted={isFormSubmitted}
				label='Agrega la Pregunta'
				parentClass='flex justify-between items-center w-full mb-2'
				placeholder='Escribe la pregunta aquí...'
				required={true}
				value={question}
				handleChange={(value: string) =>
					setQuestion(value.replace(/(<p><br><\/p>)/g, ''))
				}
			/>
			<TextInputForm
				id='answer'
				label='Respuesta correcta'
				placeholder='¿Cuál es la respuesta correcta?'
				value={answer}
				handleChange={(e: string) => setAnswer(e)}
			/>
			<TextInputForm
				id='option1'
				label='Opción 1'
				placeholder='¿Cuál es la opción 1?'
				value={option1}
				handleChange={(e: string) => setOption1(e)}
			/>
			<TextInputForm
				id='option2'
				label='Opción 2'
				placeholder='¿Cuál es la opción 2?'
				value={option2}
				handleChange={(e: string) => setOption2(e)}
			/>
			<TextInputForm
				id='option3'
				label='Opción 3'
				placeholder='¿Cuál es la opción 3?'
				value={option3}
				handleChange={(e: string) => setOption3(e)}
			/>
			<DescriptionTextArea
				label='Agrega la explicación'
				id='explanation'
				infoMessage='Escribe la explicación de la pregunta...'
				parentClass='flex justify-between items-center w-full mb-2'
				placeholder='Escribe la explicación aquí...'
				required={true}
				handleChange={(value: string) =>
					setExplanation(value.replace(/(<p><br><\/p>)/g, ''))
				}
				heightInPx='250'
				value={explanation}
				isFormSubmitted={isFormSubmitted}
			/>
			<DescriptionTextArea
				label='Agrega la(s) fuente(s)'
				id='fuente'
				infoMessage='Agrega la(s) fuente(s)la explicación de la pregunta...'
				parentClass='flex justify-between items-center w-full mb-2'
				placeholder='Escribe la(s) fuente(s) aquí...'
				required={true}
				handleChange={(value: string) =>
					setFuentes(value.replace(/(<p><br><\/p>)/g, ''))
				}
				heightInPx='80'
				value={fuentes}
				isFormSubmitted={isFormSubmitted}
			/>
			<InputCheckbox
				id='showImageInQuestion'
				label='Mostrar imagen en la pregunta'
				labelAdditionlInfo='(vs la explicación)'
				isChecked={showImageInQuestion}
				tooltip='Si la pregunta tiene una imagen, selecciona esta opción para mostrarla en la pregunta. De lo contrario la imagen sera mostrada en la explicación.'
				handleChange={(e) => setShowImageInQuestion(e.target.checked)}
			/>
			<UploadFile
				onUpload={(imgPath: string) => setImagePath(imgPath)}
				storageFolder='question-images'
				fileName={imagePath}
				fileType='image'
			/>
			<UploadFile
				onUpload={(path: string) => setAudioPath(path)}
				storageFolder='question-audio-files'
				fileName={audioPath}
				fileType='audio'
			/>
			{/* CATEGORY SELECTIONS */}
			<div className='my-2'>
				<Headline title='Tags' type='h6' />
				<div className='grid grid-cols-4 gap-1'>
					{categories
						?.filter((c) => c.questionBank === questionBankID)
						.map((c) => {
							return (
								<InputCheckbox
									key={c.id}
									id={c.id}
									label={c.name}
									isChecked={tags.includes(c.id)}
									parentClass='relative inline-flex items-start my-1 2xl:w-full w-1/2'
									handleChange={handleTags}
								/>
							);
						})}
				</div>
			</div>

			<SubmitChanges
				showButtons={isFormValid}
				cleanupState={clearForm}
				handleSubmit={(e: SyntheticEvent) => handleSubmit(e)}
				disabled={false}
			/>
		</form>
	);
};
export default QuestionBuilderForm;
